import React from 'react'
import { cn } from '@/lib/utils'
import type { MetricCategory } from './metricHelpers'
import {
    formatMetricValue,
    programTargetForCategory,
    toleranceBandForCategory,
    unitForCategory,
    valueStatus,
} from './metricHelpers'
import type { System } from '../../context/SystemContext'
import { MetricSparkline } from './MetricSparkline'

export type MetricTileProps = {
    system: System
    name: string
    deviceType: string
    category: MetricCategory
    displayValue: number | null
    history: number[]
    programType?: string
}

export function MetricTile({
    system,
    name,
    deviceType,
    category,
    displayValue,
    history,
    programType,
}: MetricTileProps) {
    const unit = unitForCategory(category)
    const target =
        programType === 'Automatic' ? programTargetForCategory(system, category) : undefined
    const band =
        programType === 'Automatic' && target !== undefined
            ? toleranceBandForCategory(system, category, target)
            : null
    const status = valueStatus(displayValue, band)

    const showTarget = programType === 'Automatic' && target !== undefined && category !== 'unknown'

    return (
        <div
            className={cn(
                'flex min-w-0 flex-col gap-2 rounded-lg border bg-zinc-950/60 p-3 shadow-inner shadow-black/20',
                status === 'warn' && 'border-amber-500/50 ring-1 ring-amber-500/20',
                status === 'ok' && 'border-emerald-900/40',
                status === 'empty' && 'border-zinc-800'
            )}
        >
            <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-zinc-100">{name}</p>
                    <p className="truncate text-[11px] text-zinc-500">{deviceType}</p>
                </div>
                <span
                    className={cn(
                        'mt-0.5 size-2 shrink-0 rounded-full',
                        status === 'ok' && 'bg-emerald-500',
                        status === 'warn' && 'bg-amber-400',
                        status === 'empty' && 'bg-zinc-600'
                    )}
                    title={status === 'warn' ? 'Outside tolerance band' : status === 'ok' ? 'Within band' : 'No reading'}
                />
            </div>

            <div className="flex flex-wrap items-end gap-x-2 gap-y-1">
                <span className="text-2xl font-semibold tabular-nums tracking-tight text-zinc-50">
                    {displayValue === null ? '—' : formatMetricValue(displayValue, category)}
                </span>
                {unit ? (
                    <span className="pb-1 text-sm font-medium text-zinc-500">{unit}</span>
                ) : null}
            </div>

            {showTarget && band ? (
                <p className="text-[11px] leading-snug text-zinc-500">
                    Target {formatMetricValue(target!, category)}
                    {unit ? ` ${unit}` : ''}
                    <span className="text-zinc-600">
                        {' '}
                        · band {formatMetricValue(band.low, category)}–{formatMetricValue(band.high, category)}
                    </span>
                </p>
            ) : showTarget ? (
                <p className="text-[11px] text-zinc-500">
                    Target {formatMetricValue(target!, category)}
                    {unit ? ` ${unit}` : ''}
                </p>
            ) : null}

            <div className="flex items-end justify-between gap-2 border-t border-zinc-800/80 pt-2">
                <MetricSparkline values={history} />
                <span className="text-[10px] text-zinc-600">Recent</span>
            </div>
        </div>
    )
}
